import type { CohortCondition, CohortFieldId } from "@unbox/api";
import { XClose } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { defaultConditionForField, NUMERIC_OPERATOR_OPTIONS } from "../condition-fields";

interface ConditionRowProps {
  condition: CohortCondition;
  fieldOptions: { id: CohortFieldId; label: string; supportingText?: string }[];
  onChange: (condition: CohortCondition) => void;
  onRemove?: () => void;
}

export function ConditionRow({ condition, fieldOptions, onChange, onRemove }: ConditionRowProps) {
  const isBetween = condition.operator === "between";

  return (
    <div className="flex flex-row items-start gap-2">
      <div className="w-80">
        <Select
          size="sm"
          hideSupportingText
          items={fieldOptions}
          selectedKey={condition.field}
          onSelectionChange={(key) => {
            if (typeof key === "string") onChange(defaultConditionForField(key as CohortFieldId));
          }}
        >
          {(item) => (
            <Select.Item id={item.id} supportingText={item.supportingText}>
              {item.label}
            </Select.Item>
          )}
        </Select>
      </div>

      <div className="w-60">
        <Select
          size="sm"
          items={NUMERIC_OPERATOR_OPTIONS}
          selectedKey={condition.operator}
          onSelectionChange={(key) => {
            if (typeof key !== "string") return;
            const operator = key as CohortCondition["operator"];
            if (operator === "between" && !Array.isArray(condition.value)) {
              onChange({ ...condition, operator, value: [0, 0] });
            } else if (operator !== "between" && Array.isArray(condition.value)) {
              onChange({ ...condition, operator, value: 0 });
            } else {
              onChange({ ...condition, operator });
            }
          }}
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>

      <div className="flex flex-row items-center gap-2">
        {isBetween ? (
          <>
            <div className="w-28">
              <Input
                size="sm"
                type="number"
                value={String(Array.isArray(condition.value) ? condition.value[0] : 0)}
                onChange={(value) => {
                  const min = Number(value);
                  const max = Array.isArray(condition.value) ? condition.value[1] : 0;
                  onChange({ ...condition, value: [Number.isNaN(min) ? 0 : min, max] });
                }}
              />
            </div>
            <span className="text-xs text-secondary">and</span>
            <div className="w-28">
              <Input
                size="sm"
                type="number"
                value={String(Array.isArray(condition.value) ? condition.value[1] : 0)}
                onChange={(value) => {
                  const max = Number(value);
                  const min = Array.isArray(condition.value) ? condition.value[0] : 0;
                  onChange({ ...condition, value: [min, Number.isNaN(max) ? 0 : max] });
                }}
              />
            </div>
          </>
        ) : (
          <div className="w-28">
            <Input
              size="sm"
              type="number"
              value={String(typeof condition.value === "number" ? condition.value : 0)}
              onChange={(value) => {
                const num = Number(value);
                onChange({ ...condition, value: Number.isNaN(num) ? 0 : num });
              }}
            />
          </div>
        )}
      </div>

      {onRemove && (
        <button
          type="button"
          className="p-2 text-quaternary hover:text-primary transition-colors"
          onClick={onRemove}
          aria-label="Remove condition"
        >
          <XClose className="size-4" />
        </button>
      )}
    </div>
  );
}
